import { useEffect, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase-config.js";

/**
 * useLiveGuestDj — subscribes to a VM host's Channel Guest state.
 *
 * Returns:
 *   liveGuestDj  — string | null. Currently-live guest DJ's uid on this
 *                  VM, or null when the VM's own playlist should play.
 *   guestDjs     — [{ djUid, djChannelName, djDisplayName, status, ... }]
 *                  every guest DJ the VM has added, in insertion order.
 *                  Filter by status === 'accepted' for the ones eligible
 *                  to go live.
 *   effectiveUid — convenience: liveGuestDj || vmUid. Callers that just
 *                  want "which uid should I read currentSong/nextSong/
 *                  chat from right now?" can use this directly and stay
 *                  correct whenever the DJ presses Go Live / Off Air.
 *   isLoading    — true until the first snapshot arrives.
 *
 * Two Firestore listeners: users/{vmUid} (for liveGuestDj) and
 * users/{vmUid}/guestDjs (for the tab list). Both tear down on unmount.
 */
export function useLiveGuestDj(vmUid) {
  const [liveGuestDj, setLiveGuestDj] = useState(null);
  const [guestDjs, setGuestDjs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!vmUid) {
      setLiveGuestDj(null);
      setGuestDjs([]);
      setIsLoading(false);
      return undefined;
    }

    let liveLoaded = false;
    let listLoaded = false;
    const maybeFinishLoading = () => {
      if (liveLoaded && listLoaded) setIsLoading(false);
    };

    const vmDocRef = doc(db, "users", vmUid);
    const unsubDoc = onSnapshot(
      vmDocRef,
      (snap) => {
        const next = snap.exists() ? snap.data().liveGuestDj || null : null;
        setLiveGuestDj(next);
        liveLoaded = true;
        maybeFinishLoading();
      },
      (err) => {
        console.warn("[useLiveGuestDj] vm doc snapshot failed:", err);
        liveLoaded = true;
        maybeFinishLoading();
      }
    );

    const guestDjsRef = collection(db, "users", vmUid, "guestDjs");
    const unsubList = onSnapshot(
      guestDjsRef,
      (snap) => {
        setGuestDjs(snap.docs.map((d) => ({ djUid: d.id, ...d.data() })));
        listLoaded = true;
        maybeFinishLoading();
      },
      (err) => {
        console.warn("[useLiveGuestDj] guestDjs snapshot failed:", err);
        listLoaded = true;
        maybeFinishLoading();
      }
    );

    return () => {
      unsubDoc();
      unsubList();
    };
  }, [vmUid]);

  const effectiveUid = liveGuestDj || vmUid || null;

  return { liveGuestDj, guestDjs, effectiveUid, isLoading };
}
